import { useEffect, useRef, useState } from 'react';
import 'twin.macro';
import { ENV } from '@config/env';
import {
  LIVE_INTERVAL_MS,
  runLiveSample,
  runSpeedTest,
  SPEED_TEST_PHASE,
} from '../../services/network-quality/speed-test';
import { WEAK_DOWNLINK_MBPS, WEAK_LATENCY_MS } from '../../services/network-quality/quality';
import { useNetworkQualityReadings } from '../../zustand/network-quality-store';

// Only the Vite dev server has somewhere to POST to (vite.config.js); a built
// image has no upload target until the backend adds one.
const UPLOAD_URL = ENV.isLocal ? '/network-probe/upload' : null;

// Speedtest-style non-linear scale: slow speeds get most of the dial, where the
// difference matters.
const SCALE = [0, 1, 5, 10, 20, 30, 50, 75, 100];
const ARC_LENGTH = Math.PI * 80;

const PHASE_LABEL = {
  [SPEED_TEST_PHASE.idle]: 'ready',
  [SPEED_TEST_PHASE.ping]: 'ping…',
  [SPEED_TEST_PHASE.download]: 'download…',
  [SPEED_TEST_PHASE.upload]: 'upload…',
  [SPEED_TEST_PHASE.done]: 'done',
  [SPEED_TEST_PHASE.error]: 'error',
};

const PHASE_COLOR = {
  [SPEED_TEST_PHASE.download]: '#22d3ee',
  [SPEED_TEST_PHASE.upload]: '#a78bfa',
};

const toDialFraction = mbps => {
  if (!mbps || mbps <= 0) {
    return 0;
  }

  const last = SCALE.length - 1;

  if (mbps >= SCALE[last]) {
    return 1;
  }

  const index = SCALE.findIndex((mark, i) => mbps >= mark && mbps < SCALE[i + 1]);
  const within = (mbps - SCALE[index]) / (SCALE[index + 1] - SCALE[index]);

  return (index + within) / last;
};

const fmt = (value, digits = 1) =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : Number.isFinite(value)
      ? value.toFixed(digits)
      : '∞';

const Gauge = ({ mbps, color, label }) => {
  const fraction = toDialFraction(mbps);

  return (
    <div tw="relative mx-auto w-[200px]">
      <svg viewBox="0 0 200 116" width="200" height="116" aria-hidden="true">
        <path
          d="M20 100 A80 80 0 0 1 180 100"
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <path
          d="M20 100 A80 80 0 0 1 180 100"
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={ARC_LENGTH}
          strokeDashoffset={ARC_LENGTH * (1 - fraction)}
          style={{ transition: 'stroke-dashoffset 200ms linear' }}
        />
        {SCALE.map((mark, index) => {
          const angle = Math.PI * (1 - index / (SCALE.length - 1));
          const x = 100 + 62 * Math.cos(angle);
          const y = 100 - 62 * Math.sin(angle);

          return (
            <text
              key={mark}
              x={x}
              y={y}
              fill="#64748b"
              fontSize="8"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {mark}
            </text>
          );
        })}
      </svg>
      <div tw="absolute inset-x-0 bottom-0 text-center">
        <div tw="text-[26px] font-bold leading-none">{fmt(mbps)}</div>
        <div tw="text-[10px] text-[#94a3b8]">Mbps · {label}</div>
      </div>
    </div>
  );
};

const Tile = ({ label, value, unit, isWeak }) => (
  <div tw="flex-1 rounded-md bg-white/5 px-2 py-1.5 text-center">
    <div tw="text-[10px] text-[#94a3b8]">{label}</div>
    <div tw="text-[15px] font-bold" style={{ color: isWeak ? '#fbbf24' : undefined }}>
      {value}
      <span tw="ml-0.5 text-[10px] font-normal text-[#94a3b8]">{unit}</span>
    </div>
  </div>
);

const LIVE_HISTORY = 30;

// Tiny trend line of the last LIVE_HISTORY readings.
const Sparkline = ({ values, color }) => {
  const points = values.filter(value => typeof value === 'number' && Number.isFinite(value));

  if (points.length < 2) {
    return <div tw="h-6" />;
  }

  const max = Math.max(...points, 1);
  const step = 100 / (LIVE_HISTORY - 1);
  const offset = LIVE_HISTORY - points.length;
  const line = points
    .map(
      (value, index) =>
        `${((offset + index) * step).toFixed(1)},${(24 - (value / max) * 22).toFixed(1)}`
    )
    .join(' ');

  return (
    <svg viewBox="0 0 100 24" preserveAspectRatio="none" tw="h-6 w-full" aria-hidden="true">
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

/**
 * Live mode: a light sample every LIVE_INTERVAL_MS while switched on, so the
 * numbers keep moving during a session. Off by default — it is real traffic.
 * Skips a round while the tab is hidden or a full test is running.
 */
const useLiveReadings = (isOn, isFullTestRunning) => {
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const pausedRef = useRef(isFullTestRunning);
  pausedRef.current = isFullTestRunning;

  useEffect(() => {
    if (!isOn) {
      return undefined;
    }

    const controller = new AbortController();
    let timeoutId = null;

    const round = async () => {
      if (document.visibilityState === 'visible' && !pausedRef.current) {
        try {
          const reading = await runLiveSample({ uploadUrl: UPLOAD_URL, signal: controller.signal });
          setError(reading.error);

          if (!reading.error) {
            setHistory(previous =>
              [...previous, { ...reading, at: Date.now() }].slice(-LIVE_HISTORY)
            );
          }
        } catch (_error) {
          return; // stopped
        }
      }

      if (!controller.signal.aborted) {
        timeoutId = window.setTimeout(round, LIVE_INTERVAL_MS);
      }
    };

    round();

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [isOn]);

  return { history, error, latest: history[history.length - 1] ?? null };
};

/**
 * Developer-only speed meter inside the network diagnostics panel: a
 * speedtest-style on-demand test (ping, download, upload) and the passive
 * monitor's live readings that drive the patient's weak-connection message.
 */
const NetworkSpeedTest = ({ sessionTraffic = null }) => {
  const [test, setTest] = useState({ phase: SPEED_TEST_PHASE.idle });
  const [isLiveOn, setIsLiveOn] = useState(false);
  const controllerRef = useRef(null);
  const readings = useNetworkQualityReadings();

  useEffect(() => () => controllerRef.current?.abort(), []);

  const isRunning = [
    SPEED_TEST_PHASE.ping,
    SPEED_TEST_PHASE.download,
    SPEED_TEST_PHASE.upload,
  ].includes(test.phase);

  const live = useLiveReadings(isLiveOn, isRunning);

  const start = () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    runSpeedTest({ uploadUrl: UPLOAD_URL, signal: controller.signal, onUpdate: setTest });
  };

  const stop = () => controllerRef.current?.abort();

  const dialMbps =
    test.phase === SPEED_TEST_PHASE.download || test.phase === SPEED_TEST_PHASE.upload
      ? test.liveMbps
      : (test.downloadMbps ?? null);
  const dialColor = PHASE_COLOR[test.phase] ?? PHASE_COLOR[SPEED_TEST_PHASE.download];

  return (
    <div tw="mb-2">
      <div tw="mt-2 flex items-center justify-between border-t border-white/20 pt-2 text-[#94a3b8]">
        <span>Speed test · {PHASE_LABEL[test.phase]}</span>
        <button
          type="button"
          onClick={isRunning ? stop : start}
          tw="rounded bg-white/10 px-2 py-0.5 text-white"
        >
          {isRunning ? 'Stop' : test.phase === SPEED_TEST_PHASE.idle ? 'Start' : 'Again'}
        </button>
      </div>

      <Gauge
        mbps={dialMbps}
        color={dialColor}
        label={test.phase === SPEED_TEST_PHASE.upload ? 'upload' : 'download'}
      />

      <div tw="mt-1 h-1 overflow-hidden rounded bg-white/10">
        <div
          tw="h-full bg-white/60"
          style={{ width: `${Math.round((isRunning ? test.progress : 0) * 100)}%` }}
        />
      </div>

      <div tw="mt-2 flex gap-1.5">
        <Tile
          label="Ping"
          value={fmt(test.pingMs, 0)}
          unit="ms"
          isWeak={test.pingMs > WEAK_LATENCY_MS}
        />
        <Tile label="Jitter" value={fmt(test.jitterMs, 0)} unit="ms" />
        <Tile
          label="Down"
          value={fmt(test.downloadMbps)}
          unit="Mbps"
          isWeak={test.downloadMbps !== null && test.downloadMbps < WEAK_DOWNLINK_MBPS}
        />
        <Tile
          label="Up"
          value={test.uploadUnavailable ? 'n/a' : fmt(test.uploadMbps)}
          unit={test.uploadUnavailable ? '' : 'Mbps'}
        />
      </div>

      {test.error && <div tw="mt-1 text-[#f87171]">{test.error}</div>}
      {!UPLOAD_URL && (
        <div tw="mt-1 text-[#94a3b8]">Upload: npm run dev only (no upload endpoint here).</div>
      )}
      <div tw="mt-1 text-[#94a3b8]">Fills the connection for ~20s — the exercise may stutter.</div>

      <div tw="mt-2 flex items-center justify-between border-t border-white/20 pt-2 text-[#94a3b8]">
        <span>Live · every {LIVE_INTERVAL_MS / 1000}s during the session</span>
        <button
          type="button"
          onClick={() => setIsLiveOn(on => !on)}
          aria-pressed={isLiveOn}
          tw="rounded px-2 py-0.5 text-white"
          style={{ background: isLiveOn ? '#16a34a' : 'rgba(255,255,255,0.1)' }}
        >
          {isLiveOn ? 'On' : 'Off'}
        </button>
      </div>
      {isLiveOn && (
        <>
          <div tw="mt-1 flex gap-1.5">
            <Tile
              label="Ping"
              value={fmt(live.latest?.pingMs, 0)}
              unit="ms"
              isWeak={live.latest?.pingMs > WEAK_LATENCY_MS}
            />
            <Tile label="Jitter" value={fmt(live.latest?.jitterMs, 0)} unit="ms" />
            <Tile
              label="Down"
              value={fmt(live.latest?.downloadMbps)}
              unit="Mbps"
              isWeak={
                typeof live.latest?.downloadMbps === 'number' &&
                live.latest.downloadMbps < WEAK_DOWNLINK_MBPS
              }
            />
            <Tile
              label="Up"
              value={UPLOAD_URL ? fmt(live.latest?.uploadMbps) : 'n/a'}
              unit={UPLOAD_URL ? 'Mbps' : ''}
            />
          </div>
          <div tw="mt-1 grid grid-cols-3 gap-1.5 text-[10px] text-[#94a3b8]">
            <div>
              down
              <Sparkline values={live.history.map(item => item.downloadMbps)} color="#22d3ee" />
            </div>
            <div>
              up
              <Sparkline values={live.history.map(item => item.uploadMbps)} color="#a78bfa" />
            </div>
            <div>
              ping
              <Sparkline values={live.history.map(item => item.pingMs)} color="#fbbf24" />
            </div>
          </div>
          {live.error && <div tw="mt-1 text-[#f87171]">{live.error}</div>}
          <div tw="mt-1 text-[#94a3b8]">
            ~768 KB per reading (~270 MB/hour) — turn off when not needed.
          </div>
        </>
      )}
      {sessionTraffic && (
        <div tw="mt-1">
          Session now: ↓ {fmt(sessionTraffic.downKBs, 1)} KB/s · ↑ {fmt(sessionTraffic.upKBs, 1)}{' '}
          KB/s (exercise socket)
        </div>
      )}

      <div tw="mt-2 border-t border-white/20 pt-2 text-[#94a3b8]">
        Monitor behind the patient message
      </div>
      <div>State: {readings.state ?? '— (CONNECTION_STATUS off or not started)'}</div>
      <div>
        Latency: {fmt(readings.latencyMs, 0)} ms · median {fmt(readings.latencyMedianMs, 0)} ms
        (weak &gt; {WEAK_LATENCY_MS})
      </div>
      <div>
        Download: {fmt(readings.downlinkMbps)} Mbps · browser {fmt(readings.browserDownlinkMbps)}{' '}
        (weak &lt; {WEAK_DOWNLINK_MBPS})
      </div>
      <div>
        Updated:{' '}
        {readings.measuredAt
          ? `${Math.round((Date.now() - readings.measuredAt) / 1000)}s ago`
          : '—'}
      </div>
    </div>
  );
};

export default NetworkSpeedTest;
